// Maintenance Mode Manager Component
import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Power, 
  Save, 
  Loader2,
  Globe,
  Clock,
  Shield,
  CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenanceManager = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    message_fr: "Site en maintenance. Nous serons bientôt de retour.",
    message_en: "Site under maintenance. We'll be back soon.",
    message_ar: "الموقع تحت الصيانة. سنعود قريباً.",
    end_date: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/maintenance`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          enabled: data.enabled || false,
          message_fr: data.message_fr || "Site en maintenance. Nous serons bientôt de retour.",
          message_en: data.message_en || "Site under maintenance. We'll be back soon.",
          message_ar: data.message_ar || "الموقع تحت الصيانة. سنعود قريباً.",
          end_date: data.end_date || ""
        });
      }
    } catch (err) {
      console.error('Error fetching maintenance settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/maintenance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success(settings.enabled ? 'Mode maintenance activé' : 'Mode maintenance désactivé');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenance = () => {
    setSettings({ ...settings, enabled: !settings.enabled });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-800">Mode Maintenance</h2>
        <p className="text-slate-500">Activez le mode maintenance pour bloquer temporairement l'accès au site</p>
      </div>

      {/* Status Card */}
      <div className={`rounded-xl p-6 border-2 transition-colors ${
        settings.enabled 
          ? 'bg-amber-50 border-amber-300' 
          : 'bg-green-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              settings.enabled ? 'bg-amber-500' : 'bg-green-500'
            }`}>
              {settings.enabled ? (
                <AlertTriangle className="w-7 h-7 text-white" />
              ) : (
                <CheckCircle className="w-7 h-7 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                {settings.enabled ? 'Mode Maintenance ACTIVÉ' : 'Site en ligne'}
              </h3>
              <p className="text-sm text-slate-600">
                {settings.enabled 
                  ? 'Les visiteurs voient la page de maintenance' 
                  : 'Le site est accessible normalement'
                }
              </p>
            </div>
          </div>
          <Button
            onClick={toggleMaintenance}
            variant={settings.enabled ? "default" : "outline"}
            className={settings.enabled ? 'bg-green-600 hover:bg-green-700' : 'border-amber-500 text-amber-700 hover:bg-amber-100'}
          >
            <Power className="w-4 h-4 mr-2" />
            {settings.enabled ? 'Désactiver' : 'Activer'}
          </Button>
        </div>
      </div>

      {/* Messages Configuration */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 space-y-4">
        <h3 className="font-medium text-slate-800 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Messages de maintenance
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Message (Français)
            </label>
            <textarea
              value={settings.message_fr}
              onChange={(e) => setSettings({ ...settings, message_fr: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Message affiché aux visiteurs francophones"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Message (Anglais)
            </label>
            <textarea
              value={settings.message_en}
              onChange={(e) => setSettings({ ...settings, message_en: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Message displayed to English visitors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Message (Arabe)
            </label>
            <textarea
              value={settings.message_ar}
              onChange={(e) => setSettings({ ...settings, message_ar: e.target.value })}
              rows={2}
              dir="rtl"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="الرسالة المعروضة للزوار العرب"
            />
          </div>
        </div>
      </div>

      {/* Schedule (Optional) */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-medium text-slate-800 flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-purple-500" />
          Planification (optionnel)
        </h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Fin prévue de la maintenance
          </label>
          <input
            type="datetime-local"
            value={settings.end_date || ''}
            onChange={(e) => setSettings({ ...settings, end_date: e.target.value })}
            className="w-full md:w-auto px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-slate-500 mt-1">
            Information affichée aux visiteurs (ne désactive pas automatiquement le mode maintenance)
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      {/* Warning */}
      {settings.enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <strong>⚠️ Attention :</strong> Le mode maintenance est activé. 
          Les visiteurs ne peuvent pas accéder au site public. 
          Vous pouvez toujours accéder au panneau d'administration.
        </div>
      )}
    </div>
  );
};

export default MaintenanceManager;
